import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonInstall = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Step 1: The Build Site</div>
                <h1>The Art of Installing Python</h1>
                <p className="hero-lead">
                    Installing Python isn't just about clicking "Next". It's about laying a solid concrete foundation for everything you will build later.
                    A bad installation leads to "It works on my machine" errors. A good one leads to professional stability.
                </p>
            </div>

            <div className="content-section chapter-intro">
                <h2>The "Foundation" Analogy</h2>
                <p>
                    Imagine you are building a house. Before you lay bricks (write code) or paint walls (design UI), you need a solid <strong>Foundation</strong>.
                </p>
                <p>
                    If you pour the concrete wrongif it's uneven or full of cracksyour entire house will eventually collapse.
                    Installing Python is pouring that concrete. If you install it incorrectly (e.g., mixing versions, ignoring PATH, using the wrong architecture),
                    your "house" of code might stand up today, but it will crumble when you try to install libraries or deploy to the cloud.
                </p>
                <p>
                    In this guide, we won't just "install" Python. We will engineer a professional development environment.
                </p>

                <div className="learning-objectives">
                    <h3>Engineering Goals</h3>
                    <ul>
                        <li><strong>The Golden Rule:</strong> Never touch the System Python.</li>
                        <li><strong>The Address Book (PATH):</strong> Demystifying the magical environment variable.</li>
                        <li><strong>Package Management:</strong> Why <a href="https://brew.sh/" target="_blank" rel="noopener noreferrer">Homebrew</a> (Mac) and <a href="https://learn.microsoft.com/en-us/windows/package-manager/winget/" target="_blank" rel="noopener noreferrer">Winget</a> (Windows) are superior to dragging installers.</li>
                        <li><strong>Verification:</strong> Proving your installation is robust.</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>1. The System Python Trap (WARNING)</h2>
                <p>
                    If you are on macOS or Linux, you already have Python installed. Open your terminal and type <code>python3 --version</code>.
                    You might see something like 3.9.6.
                </p>
                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>DO NOT USE THIS PYTHON.</strong> This version belongs to your Operating System.
                        Apple and Linux use it to run internal tasks (like printing services or window management).
                        If you install libraries into this Python and mess it up, you can literally break your OS (e.g., your sound might stop working!).
                    </div></ProTip>
                <p>
                    <strong>The Solution:</strong> We always install a <em>second</em>, parallel version of Python specifically for development.
                    This is your "Sandbox". You can break it, delete it, and reinstall it without hurting your computer.
                </p>
            </div>

            <div className="content-section">
                <h2>2. Installation: The Professional Way</h2>
                <p>
                    While you <em>can</em> go to <a href="https://www.python.org/downloads/" target="_blank" rel="noopener noreferrer">python.org</a> and download an installer, pros often use <strong>Package Managers</strong>.
                    Package managers are like "App Stores" for developersthey handle updates and dependencies automatically.
                </p>

                <h3>Option A: Windows (The Modern Way)</h3>
                <p>
                    Microsoft now includes a powerful package manager called <strong>Winget</strong>. It allows you to install tools from the command line, just like on Linux.
                </p>
                <div className="code-example">
                    <div className="code-header">
                        <span>PowerShell (Run as Administrator)</span>
                    </div>
                    <CodeBlock language="powershell" code={`# 1. Search for Python versions
winget search Python.Python.3

# 2. Install the latest stable version (e.g., 3.12)
winget install Python.Python.3.12

# Note: This automatically adds Python to your PATH 
# and sets up the correct permissions.`} />
                </div>

                <h3>Option B: Windows (The Classic Way)</h3>
                <p>
                    If you prefer the graphical installer:
                </p>
                <ol>
                    <li>Download the "Windows installer (64-bit)" from Python.org.</li>
                    <li><strong>CRITICAL STEP:</strong> On the first screen, check the box <strong>"Add python.exe to PATH"</strong>.</li>
                    <li>Click "Install Now".</li>
                    <li>To check if it worked, open a <em>new</em> Command Prompt and type <code>python --version</code>.</li>
                </ol>

                <hr className="section-divider" />

                <h3>Option C: macOS (The Homebrew Way)</h3>
                <p>
                    On a Mac, the gold standard is <a href="https://brew.sh/" target="_blank" rel="noopener noreferrer">Homebrew</a>.
                    If you don't have it, install it immediately. It is essential for modern Mac development.
                </p>
                <div className="code-example">
                    <div className="code-header">
                        <span>Terminal (macOS)</span>
                    </div>
                    <CodeBlock language="bash" code={`# 1. Update Homebrew's database
brew update

# 2. Install Python 3
brew install python

# 3. Verify it's the Homebrew version (not the System version)
which python3
# CORRECT OUTPUT: /opt/homebrew/bin/python3
# INCORRECT (System): /usr/bin/python3`} />
                </div>
            </div>

            <div className="content-section">
                <h2>3. Demystifying "The PATH"</h2>
                <p>
                    You will hear this term constantly: "Is it in your PATH?" "Add it to your PATH." But what <em>is</em> the PATH?
                </p>

                <div className="analogy-box">
                    <h3> The "Address Book" Analogy</h3>
                    <p>
                        Imagine your computer's terminal is a courier. You shout, <strong>"Run Python!"</strong>
                    </p>
                    <p>
                        The courier (Terminal) doesn't know where "Python" lives. It has to look up the address.
                        The <strong>PATH</strong> is simply a list of foldersan "Address Book"that the courier checks one by one.
                    </p>
                    <ul>
                        <li><strong>Step 1:</strong> Checks Folder A ("C:\Windows"). Is Python there? No.</li>
                        <li><strong>Step 2:</strong> Checks Folder B ("C:\Program Files"). Is Python there? No.</li>
                        <li><strong>Step 3:</strong> Checks Folder C ("C:\Python312"). <strong>Found it!</strong></li>
                    </ul>
                    <p>
                        If you forget to "Add to PATH", the courier checks every folder in its book, finds nothing, and yells back:
                        <code>'python' is not recognized as an internal or external command</code>.
                    </p>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Checking Your PATH (Windows)</span>
                    </div>
                    <CodeBlock language="powershell" code={`# View the entire Address Book
$env:PATH -split ';'

# This lists every folder your terminal checks. 
# Make sure your Python folder is in this list!`} />
                </div>
            </div>

            <div className="content-section">
                <h2>6. Verification: The "Smoke Test"</h2>
                <p>
                    Installing isn't enough. We must verify. This is called a "Smoke Test" in engineeringturn it on and make sure no smoke comes out.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>The Universal Verification Check</span>
                    </div>
                    <CodeBlock language="bash" code={`# 1. Basic Version Check
python3 --version  # Mac/Linux
python --version   # Windows

# 2. Check the "pip" installer
# pip is the tool we use to download libraries like numpy or pandas
pip --version

# 3. Check the Location (To ensure it's not System Python)
where python      # Windows PowerShell
which python3     # Mac/Linux`} />
                </div>
            </div>

            <div className="content-section">
                <h2>7. Troubleshooting Common Errors</h2>
                <p>
                    Even professionals hit snags. Here are the top 3 installation errors and how to fix them.
                </p>

                <div className="troubleshooting-grid">
                    <div className="troubleshoot-item">
                        <h4> "pip is not recognized..."</h4>
                        <p><strong>Cause:</strong> Python is in your PATH, but the `Scripts` folder isn't.</p>
                        <p><strong>Fix:</strong> Find your Python folder, look for a subfolder called `Scripts` (where pip.exe lives), and add that full path to your Environment Variables.</p>
                    </div>

                    <div className="troubleshoot-item">
                        <h4> "Permission Denied" (Linux/Mac)</h4>
                        <p><strong>Cause:</strong> You are trying to install a global package without `sudo`, or you are fighting the System Python permissions.</p>
                        <p><strong>Fix:</strong> Never use `sudo pip install`. Instead, use `pip install --user package_name` to install it just for your user account.</p>
                    </div>

                    <div className="troubleshoot-item">
                        <h4> "SSL Certificate verification failed"</h4>
                        <p><strong>Cause:</strong> (macOS specific) Python needs root certificates that weren't installed automatically.</p>
                        <p><strong>Fix:</strong> Go to `/Applications/Python 3.x/` and run the file named `Install Certificates.command`.</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>5. Next Level: Managing Multiple Versions</h2>
                <p>
                    Eventually, you will work on a project that requires Python 3.8 (because of an old library), but your other project needs Python 3.12 (for speed).
                    How do you swap between them?
                </p>
                <table className="premium-table">
                    <thead>
                        <tr>
                            <th>Tool</th>
                            <th>OS</th>
                            <th>Why use it?</th>
                            <th>Link</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><strong>pyenv</strong></td>
                            <td>Mac/Linux</td>
                            <td>The industry standard. Swaps versions instantly per folder.</td>
                            <td><a href="https://github.com/pyenv/pyenv" target="_blank" rel="noopener noreferrer">Reference</a></td>
                        </tr>
                        <tr>
                            <td><strong>pyenv-win</strong></td>
                            <td>Windows</td>
                            <td>The Windows port of pyenv. Essential for serious Windows devs.</td>
                            <td><a href="https://github.com/pyenv-win/pyenv-win" target="_blank" rel="noopener noreferrer">Reference</a></td>
                        </tr>
                        <tr>
                            <td><strong>Docker</strong></td>
                            <td>All</td>
                            <td>Runs Python in a completely isolated container (Advanced).</td>
                            <td><a href="https://www.docker.com/" target="_blank" rel="noopener noreferrer">Reference</a></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="content-section">
                <h2>8. A Brief History: The Python 2 vs 3 War</h2>
                <p>
                    You might wonder: "Why do we type <code>python3</code> on Mac/Linux but just <code>python</code> on Windows?"
                </p>
                <p>
                    This is the scar tissue from one of the biggest events in open-source history. In 2008, Python creators decided to fix fundamental flaws in the language (like how it handled text vs. bytes).
                    However, the changes were <strong>backwards incompatible</strong>. Code written for Python 2 would crash on Python 3.
                </p>
                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>The "Split" Decade:</strong> For over 10 years, the community was split. Operating systems kept shipping Python 2 because system scripts depended on it.
                        Developers installed Python 3 alongside it. To avoid conflict, the executable was named <code>python3</code>.
                    </div></ProTip>
                <p>
                    Today, Python 2 is officially dead (EOL as of 2020). However, the naming convention persists on Unix-based systems. on Windows, since it doesn't use Python for system tasks,
                    the installer defaults to just <code>python.exe</code>.
                </p>
            </div>

            <div className="content-section">
                <h2>9. Your First Interaction: The REPL</h2>
                <p>
                    Once installed, you have a superpower: The <strong>REPL</strong> (Read-Eval-Print Loop). It allows you to talk to Python directly.
                </p>
                <p>
                    Open your terminal and just type <code>python</code> (or <code>python3</code>). You will see the "Chevron Prompt" (<code>&gt;&gt;&gt;</code>).
                </p>
                <div className="code-example">
                    <div className="code-header">
                        <span>Interactive Python Session</span>
                    </div>
                    <CodeBlock language="python" code={`$ python
Python 3.12.2 (tags/v3.12.2:6abddd9, Feb  6 2024, 21:26:36) [MSC v.1937 64 bit (AMD64)] on win32
Type "help", "copyright", "credits" or "license" for more information.

>>> 2 + 2
4
>>> name = "FileFusion"
>>> print(f"Hello {name}")
Hello FileFusion
>>> exit()  # To leave the matrix`} />
                </div>
                <p>
                    <strong>When to use this?</strong> The REPL is perfect for "micro-experiments". Forgot how a function works? Test it here instantly.
                    Don't write a whole file to check if <code>2**10</code> is 1024 or 2048.
                </p>
            </div>

            <div className="content-section">
                <h2>10. Running Your First Script</h2>
                <p>
                    While the REPL is great for testing, we write software in files. A Python file is just a text file ending in <code>.py</code>.
                </p>
                <div className="code-example">
                    <div className="code-header">
                        <span>hello.py</span>
                    </div>
                    <CodeBlock language="python" code={`# Create a file named 'hello.py'
import sys
import { ProTip } from '../../components/LessonUI';

print("Hello from Python implementation:", sys.implementation.name)
print("Running on version:", sys.version)`} />
                </div>
                <p>
                    To run this, you pass the file path to your interpreter. This is exactly what your IDE (like VS Code) does behind the scenes when you click "Run".
                </p>
                <div className="code-example">
                    <div className="code-header">
                        <span>Terminal Execution</span>
                    </div>
                    <CodeBlock language="bash" code={`# Absolute Path
python C:\\Users\\You\\Desktop\\hello.py

# Relative Path (if you are in the folder)
python hello.py`} />
                </div>
            </div>

            <div className="content-section key-takeaways">
                <h2> Key Takeaways</h2>
                <div className="takeaway-grid">
                    <div className="takeaway-item">
                        <h3>1. Respect the System</h3>
                        <p>
                            Never touch the OS-bundled Python. Treat it like a load-bearing wall in your house.
                        </p>
                    </div>

                    <div className="takeaway-item">
                        <h3>2. Automate Installs</h3>
                        <p>
                            Use Winget (Windows) or Homebrew (Mac) to keep Python updated automatically.
                        </p>
                    </div>

                    <div className="takeaway-item">
                        <h3>3. The PATH is Key</h3>
                        <p>
                            Understanding the PATH variable solves 90% of beginner "It's not working" errors.
                        </p>
                    </div>

                    <div className="takeaway-item">
                        <h3>4. Verify, Verify, Verify</h3>
                        <p>
                            Always run <code>python --version</code> before starting work to confirm you are in the right environment.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PythonInstall;

